import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Activity, Users, AlertTriangle, 
  Clock, RefreshCw, Eye, UserX, ShieldCheck, CreditCard, 
  Settings } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const ACTION_ICONS = {
  delete_user: <UserX size={14} />,
  change_role: <ShieldCheck size={14} />,
  plan_change: <CreditCard size={14} />,
  login: <Eye size={14} />,
  settings_change: <Settings size={14} />,
};

const ACTION_COLORS = {
  delete_user: 'var(--error)',
  change_role: 'var(--warning)',
  plan_change: 'var(--info)',
  login: 'var(--success)',
  settings_change: 'var(--text-secondary)',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalLogs, setTotalLogs] = useState(0);

  useEffect(() => {
    loadAuditLogs();
    loadStats();
  }, [page]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/audit-logs', { params: { page, limit: 50 } });
      if (page === 1) {
        setLogs(res.data.logs);
      } else {
        setLogs(prev => [...prev, ...res.data.logs]);
      }
      setHasMore(res.data.has_more);
      setTotalLogs(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/api/admin/audit-stats');
      setStats(res.data);
    } catch {}
  };

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-2xl)' }}>
          <StatCard icon={<Activity size={22} />} label="Total Logs" value={stats.total_logs} color="var(--info)" />
          <StatCard icon={<Clock size={22} />} label="Last 24 Hours" value={stats.recent_24h} color="var(--success)" />
          <StatCard icon={<Shield size={22} />} label="Actions Tracked" value={stats.actions?.length || 0} color="var(--warning)" />
          <StatCard icon={<Users size={22} />} label="Active Admins" value={stats.top_admins?.length || 0} color="var(--accent-primary)" />
        </div>
      )}

      {/* Audit Log Table */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--space-2xl)',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            Audit Trail · {totalLogs} entries
          </h3>
          <button onClick={() => { setPage(1); loadAuditLogs(); }} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
            background: 'transparent', color: 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-primary)' }}>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Admin</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Target</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const icon = ACTION_ICONS[log.action] || <Activity size={14} />;
                const color = ACTION_COLORS[log.action] || 'var(--text-secondary)';
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <td style={tdStyle}>
                      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {new Date(log.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                        {new Date(log.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span 
                        onClick={() => { if (log.admin_email && log.admin_id) navigate(`/dashboard/admin/users/${log.admin_id}`); }}
                        style={{ color: 'var(--accent-primary)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}>
                        {log.admin_email}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, fontWeight: 500 }}>
                        {icon} {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {log.target_type === 'user' && log.target_id ? (
                        <span onClick={() => { navigate(`/dashboard/admin/users/${log.target_id}`); }}
                          style={{ color: 'var(--accent-primary)', cursor: 'pointer', textDecoration: 'underline' }}>
                          {log.target_name || log.target_id}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-primary)' }}>{log.target_name || log.target_id || ', '}</span>
                      )}
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 11, marginLeft: 8 }}>({log.target_type})</span>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 300 }}>
                      {log.action === 'reset_usage' && log.old_value && log.new_value ? (
                        <div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                            Searches: <span style={{ color: 'var(--error)' }}>{JSON.parse(log.old_value).searches}</span> → <span style={{ color: 'var(--success)' }}>{JSON.parse(log.new_value).searches}</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                            AI Scores: <span style={{ color: 'var(--error)' }}>{JSON.parse(log.old_value).ai_scores}</span> → <span style={{ color: 'var(--success)' }}>{JSON.parse(log.new_value).ai_scores}</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {log.old_value && <span style={{ color: 'var(--error)', fontSize: 11, display: 'block' }}>← {typeof log.old_value === 'string' ? log.old_value : JSON.stringify(log.old_value)}</span>}
                          {log.new_value && <span style={{ color: 'var(--success)', fontSize: 11, display: 'block' }}>→ {typeof log.new_value === 'string' ? log.new_value : JSON.stringify(log.new_value)}</span>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <button onClick={() => setPage(prev => prev + 1)} disabled={loading} style={{
              padding: '10px 24px', borderRadius: 10, border: '1px solid var(--border-primary)',
              background: 'transparent', color: 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}>
              {loading ? 'Loading...' : `Load More (${totalLogs - logs.length} remaining)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', textAlign: 'center',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}

const thStyle = { padding: '12px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 14px', whiteSpace: 'nowrap' };